/* Claude 4.71 — DRAWER UPSELLS TIED TO THE BASKET OFFER (Option C, Nov 2026)
 *
 * WHAT IT DOES. The recommendations used to sit behind the small collapsed
 * "Complete the look" link, with 32px thumbnails that often came up blank.
 * This shows up to 3 of them, always open, in a tinted box right under the
 * basket lines, with photos, and ties each one to the next basket offer:
 *
 *   £301 to 10% off — get there with
 *   [photo] Pair of Sheepskin Cushions      £109 · then £192 to go   [+]
 *
 * Once the top offer is reached (or no offer is on) the heading falls back
 * to the drawer's upsells heading ("Complete the look") and rows show price.
 *
 * WHY A SEPARATE FILE. Same reason as claude-drawer-tiers.js:
 * assets/claude-cart-drawer.js is ~51KB, so this patches the drawer's
 * prototype instead of retyping it. It changes exactly two methods:
 *
 *   renderCtlRows()  — the collapsed panel no longer repeats the products
 *                      (box / gift-wrap offer rows in it are untouched)
 *   paintCtl()       — runs the original, hides the "Complete the look" link
 *                      when its panel has nothing left in it, then draws the
 *                      new box. paintCtl runs on open and on every re-render.
 *
 * Products: the same recommendations call and cache the drawer already uses
 * (complementary, then related), first 3 available ones not in the basket.
 * Offer amounts: the data-autoapply-* attributes on #ClaudeCartDrawer-Inner
 * (theme settings), measured against real products only — packaging and
 * tracked delivery excluded, the same sum claude-drawer-tiers.js uses.
 * The + button reuses the drawer's own data-ccd-add handler.
 *
 * IF claude-cart-drawer.js IS EVER REWRITTEN, fold this in and delete this
 * file, its stylesheet and both tags in sections/claude-cart-drawer.liquid.
 */
(() => {
  const ADDON_FALLBACK = ['luxury-gift-box', 'cardboard-package', 'tracked-shipping'];
  const PLUS_ICON =
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M8 2v12M2 8h12"/></svg>';

  const money = (cents) => {
    const pounds = Math.floor(cents / 100).toLocaleString('en-GB');
    const pence = Math.round(cents % 100);
    return pence === 0 ? `£${pounds}` : `£${pounds}.${String(pence).padStart(2, '0')}`;
  };
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const offerLabel = (code) => {
    const m = String(code || '').match(/\d+/);
    return m ? `${m[0]}% off` : 'your discount';
  };

  const patch = (Drawer) => {
    const proto = Drawer.prototype;
    if (proto.__ccdOfferUpsells) return false;
    proto.__ccdOfferUpsells = true;

    proto.renderCtlRows = async function (panel) {
      if (panel) panel.innerHTML = '';
    };

    const origPaintCtl = proto.paintCtl;
    proto.paintCtl = function () {
      const out = origPaintCtl.apply(this, arguments);
      const wrap = this.querySelector('[data-ccd-ctl-wrap]');
      if (wrap) {
        const btn = wrap.querySelector('[data-ccd-ctl-toggle]');
        const hasOffer = !!wrap.querySelector('[data-ccd-ctl-panel] .ccd-orow--pack');
        if (btn) btn.hidden = !hasOffer;
        if (!hasOffer) {
          const outer = wrap.querySelector('[data-ccd-ctl-panel]');
          if (outer) outer.hidden = true;
        }
      }
      this.renderOfferUpsells();
      return out;
    };

    proto.renderOfferUpsells = async function () {
      const inner = this.querySelector('#ClaudeCartDrawer-Inner');
      if (!inner || inner.dataset.itemCount === '0') return;
      const anchor = inner.querySelector('[data-ccd-ctl-wrap]');
      const sourceId = inner.dataset.sourceProduct;
      if (!anchor || !sourceId) return;

      const token = (this._oupsToken = (this._oupsToken || 0) + 1);
      let box = inner.querySelector('.ccd-oups');
      if (!box) {
        box = document.createElement('div');
        box.className = 'ccd-oups';
        box.hidden = true;
        anchor.parentNode.insertBefore(box, anchor);
      }

      let inCart = [];
      try { inCart = JSON.parse(inner.dataset.cartProducts || '[]'); } catch (e) { /* noop */ }

      if (!this.upsellCache) this.upsellCache = {};
      let products = this.upsellCache[sourceId];
      if (!products) {
        try {
          products = await this.fetchRecommendations(sourceId, 'complementary');
          if (!products.length) products = await this.fetchRecommendations(sourceId, 'related');
          this.upsellCache[sourceId] = products;
        } catch (e) { return; }
      }
      const cart = typeof this.fetchCart === 'function' ? await this.fetchCart() : null;
      if (token !== this._oupsToken || !box.isConnected) return;

      const items = (products || []).filter((p) => p.available && !inCart.includes(p.id)).slice(0, 3);
      const topBar = inner.querySelector('.ccd-tierbar');
      if (!items.length) {
        box.hidden = true;
        if (topBar) topBar.hidden = false;
        return;
      }

      const handles = typeof this.addonHandles === 'function' ? this.addonHandles() : ADDON_FALLBACK;
      const real = cart
        ? (cart.items || [])
            .filter((i) => handles.indexOf(i.handle) === -1)
            .reduce((sum, i) => sum + (i.original_line_price || 0), 0)
        : 0;

      const tiers = [
        [inner.dataset.autoapplyLowCode, parseInt(inner.dataset.autoapplyLowMin || '0', 10)],
        [inner.dataset.autoapplyCode, parseInt(inner.dataset.autoapplyMin || '0', 10)],
      ].filter((t) => t[0] && t[1] > 0).sort((a, b) => a[1] - b[1]);
      const next = cart ? tiers.find((t) => real < t[1]) : null;

      const toggle = inner.querySelector('[data-ccd-ctl-toggle]');
      const fallbackHeading = (toggle && toggle.firstChild && toggle.firstChild.textContent.trim()) || 'Complete the look';
      const heading = next
        ? `${money(next[1] - real)} to ${offerLabel(next[0])} — get there with`
        : fallbackHeading;

      const rows = items.map((p) => {
        let img = p.featured_image || '';
        if (img) img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=160';
        let sub = money(p.price);
        if (next) {
          const after = next[1] - real - p.price;
          sub += after > 0 ? ` · then ${money(after)} to go` : ` · unlocks ${offerLabel(next[0])}`;
        }
        const single = p.variants && p.variants.length === 1 && p.variants[0].available;
        const action = single
          ? `<button type="button" class="ccd-oups-add" data-ccd-add="${p.variants[0].id}" aria-label="Add ${esc(p.title)} to basket">${PLUS_ICON}</button>`
          : `<a class="ccd-oups-add" href="${esc(p.url)}" aria-label="Choose options for ${esc(p.title)}">${PLUS_ICON}</a>`;
        return (
          `<div class="ccd-oups-row">` +
          `<a class="ccd-oups-img" href="${esc(p.url)}" tabindex="-1" aria-hidden="true">` +
          (img ? `<img src="${esc(img)}" alt="" width="104" height="104" decoding="async">` : '<span class="ccd-oups-noimg"></span>') +
          `</a>` +
          `<div class="ccd-oups-info">` +
          `<a class="ccd-oups-name" href="${esc(p.url)}">${esc(p.title)}</a>` +
          `<span class="ccd-oups-sub">${sub}</span>` +
          `</div>` +
          action +
          `</div>`
        );
      }).join('');

      /* 4.72 — Option C has ONE offer bar: while an offer is still to reach,
         the top tier bar hides and its progress line sits in this box. Once
         the top offer is reached the top bar shows again ("10% off applied"). */
      let track = '';
      if (next) {
        const top = tiers[tiers.length - 1][1];
        const pct = Math.max(0, Math.min(100, Math.round((real / top) * 100)));
        const step = tiers.length > 1 ? Math.round((tiers[0][1] / top) * 100) : 0;
        track =
          `<div class="ccd-oups-track" aria-hidden="true"><span class="ccd-oups-fill" style="width:${pct}%"></span>` +
          (step ? `<span class="ccd-oups-step" style="left:${step}%"></span>` : '') +
          `</div>`;
      }
      if (topBar) topBar.hidden = !!next;

      box.innerHTML =
        `<p class="ccd-oups-heading${next ? '' : ' ccd-oups-heading--plain'}">${esc(heading)}</p>` +
        track +
        `<div class="ccd-oups-list">${rows}</div>`;
      box.hidden = false;
    };
    return true;
  };

  if (!window.customElements) return;
  customElements.whenDefined('cart-drawer').then(() => {
    if (!patch(customElements.get('cart-drawer'))) return;
    const el = document.getElementById('ClaudeCartDrawer');
    if (el && typeof el.paintCtl === 'function') el.paintCtl();
  });
})();
